import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, UserCircle, Shield, Users as UsersIcon, Briefcase, Eye, EyeOff, KeyRound } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminUsers() {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const isEditor = currentUser?.role === 'editor';

  const ROLES = {
    admin:   { label: t('users.role.admin'),   color: 'bg-red-100 text-red-800',    icon: Shield },
    manager: { label: t('users.role.manager'), color: 'bg-blue-100 text-blue-800',  icon: Briefcase },
    editor:  { label: t('users.role.editor'),  color: 'bg-purple-100 text-purple-800', icon: Briefcase },
    broker:  { label: t('users.role.broker'),  color: 'bg-green-100 text-green-800', icon: UsersIcon },
  };
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(null); // user object

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const r = await axios.get(`${API}/users`);
      setUsers(r.data);
    } catch { toast.error('Грешка при зареждане'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Изтриване на потребител "${userName}"?`)) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('Потребителят е изтрит');
      fetchUsers();
    } catch { toast.error('Грешка при изтриване'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-2">{t('users.title')}</h1>
          <p className="text-muted-foreground">Управление на потребителските акаунти и роли</p>
        </div>
        <Button onClick={() => setCreateModal(true)} data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" />Нов потребител
        </Button>
      </div>

      <div className="admin-card">
        {users.length === 0 ? (
          <div className="text-center py-12">
            <UserCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Няма добавени потребители</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Име</th><th>{t('admin.email')}</th><th>{t('admin.phone')}</th><th>{t('admin.role')}</th><th>{t('admin.created_at')}</th><th>{t('admin.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const RoleIcon = ROLES[user.role]?.icon || UsersIcon;
                  return (
                    <tr key={user.id} className={user.email === 'admin@greencastle.bg' ? 'opacity-50 bg-gray-50' : ''}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-medium text-primary">
                            {user.name?.charAt(0) || '?'}
                          </div>
                          <span className="font-medium">{user.name}</span>
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td>{user.phone || '-'}</td>
                      <td>
                        <Badge className={`flex items-center gap-1 w-fit ${ROLES[user.role]?.color || ''}`}>
                          <RoleIcon className="w-3 h-3" />
                          {ROLES[user.role]?.label || user.role}
                        </Badge>
                      </td>
                      <td className="text-muted-foreground">
                        {new Date(user.created_at).toLocaleDateString('bg-BG')}
                      </td>
                      <td>
                        {user.email === 'admin@greencastle.bg' ? (
                          <span className="text-xs text-muted-foreground italic px-2">{t('users.system_account')}</span>
                        ) : (
                          <div className="flex items-center gap-1">
                            {(!isEditor || user.role === 'broker') && (
                              <Button variant="ghost" size="sm" onClick={() => setEditModal(user)} title="Редактирай">
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50"
                              onClick={() => handleDelete(user.id, user.name)}
                              disabled={user.role === 'admin' && users.filter(u => u.role === 'admin').length === 1}
                              title="Изтрий">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <CreateUserModal open={createModal} onClose={() => setCreateModal(false)} onSaved={fetchUsers} />

      {/* Edit Modal */}
      {editModal && (
        <EditUserModal user={editModal} onClose={() => setEditModal(null)} onSaved={fetchUsers} />
      )}
    </div>
  );
}

function CreateUserModal({ open, onClose, onSaved }) {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const isEditor = currentUser?.role === 'editor';
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'broker', phone: '' });
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/users`, form);
      toast.success('Потребителят е създаден');
      onClose();
      onSaved();
      setForm({ name: '', email: '', password: '', role: 'broker', phone: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Грешка при създаване');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{t('users.new_user')}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><Label>Име *</Label>
            <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="mt-1" /></div>
          <div><Label>Имейл *</Label>
            <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required className="mt-1" /></div>
          <div><Label>Парола *</Label>
            <div className="relative mt-1">
              <Input type={showPwd ? 'text' : 'password'} value={form.password}
                onChange={e => setForm({...form, password: e.target.value})} required minLength={8} className="pr-10" />
              <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div><Label>Телефон</Label>
            <Input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="mt-1" /></div>
          <div><Label>Роля *</Label>
            <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="broker">{t('users.role.broker')}</SelectItem>
                {!isEditor && <SelectItem value="editor">{t('users.role.editor')}</SelectItem>}
                {!isEditor && <SelectItem value="manager">{t('users.role.manager')}</SelectItem>}
                {!isEditor && <SelectItem value="admin">{t('users.role.admin')}</SelectItem>}
              </SelectContent>
            </Select>
            {isEditor && (
              <p className="text-xs text-muted-foreground mt-1">Редакторите могат да създават само Брокери.</p>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>{t('admin.cancel')}</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Запазване...' : 'Създай'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditUserModal({ user, onClose, onSaved }) {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const isEditor = currentUser?.role === 'editor';
  const [form, setForm] = useState({
    name: user.name || '',
    email: user.email || '',
    phone: user.phone || '',
    role: user.role || 'broker',
    new_password: '',
    confirm_password: '',
  });
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [changePassword, setChangePassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (changePassword && form.new_password !== form.confirm_password) {
      toast.error('Паролите не съвпадат');
      return;
    }
    if (changePassword && form.new_password.length < 8) {
      toast.error('Паролата трябва да е поне 8 символа');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        role: form.role,
      };
      if (changePassword && form.new_password) {
        payload.new_password = form.new_password;
      }
      await axios.put(`${API}/users/${user.id}`, payload);
      toast.success('Потребителят е обновен');
      onClose();
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Грешка при обновяване');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-medium text-primary">
              {user.name?.charAt(0)}
            </div>
            Редактиране — {user.name}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Име *</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="mt-1" /></div>
            <div><Label>Телефон</Label>
              <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="mt-1" /></div>
          </div>
          <div><Label>Имейл *</Label>
            <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required className="mt-1" /></div>
          <div><Label>Роля</Label>
            <Select value={form.role} onValueChange={v => setForm({...form, role: v})} disabled={isEditor}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="broker">Брокер</SelectItem>
                {!isEditor && <SelectItem value="editor">Редактор</SelectItem>}
                {!isEditor && <SelectItem value="manager">Мениджър</SelectItem>}
                {!isEditor && <SelectItem value="admin">Администратор</SelectItem>}
              </SelectContent>
            </Select>
            {isEditor && (
              <p className="text-xs text-muted-foreground mt-1">Редакторите могат да редактират само брокери.</p>
            )}
          </div>

          {/* Password section */}
          <div className="border-t border-border pt-4">
            <button type="button" onClick={() => setChangePassword(!changePassword)}
              className="flex items-center gap-2 text-sm font-medium text-[#1B5E37] hover:underline">
              <KeyRound className="w-4 h-4" />
              {changePassword ? 'Отказ от смяна на парола' : 'Смяна на парола'}
            </button>
            {changePassword && (
              <div className="space-y-3 mt-3">
                <div><Label>Нова парола</Label>
                  <div className="relative mt-1">
                    <Input type={showPwd ? 'text' : 'password'} value={form.new_password}
                      onChange={e => setForm({...form, new_password: e.target.value})}
                      placeholder="Минимум 8 символа" className="pr-10" />
                    <button type="button" onClick={() => setShowPwd(!showPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div><Label>Потвърди паролата</Label>
                  <Input type="password" value={form.confirm_password}
                    onChange={e => setForm({...form, confirm_password: e.target.value})}
                    placeholder="Повторете паролата" className="mt-1" />
                  {form.confirm_password && form.new_password !== form.confirm_password && (
                    <p className="text-xs text-red-500 mt-1">Паролите не съвпадат</p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>{t('admin.cancel')}</Button>
            <Button type="submit" disabled={saving} className="bg-[#1B5E37] hover:bg-[#154a2c]">
              {saving ? 'Запазване...' : 'Запази промените'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
